/**
 * Cálculo de prazo de aprovação no cliente.
 * Espelho de vacationAlertSchedule.ts para o front-end.
 *
 * Prazo de aprovação = início das férias − 30 dias
 */

import { createDateSafe } from '@/utils/dateUtils';
import { differenceInCalendarDays, subDays, format, isValid } from 'date-fns';

export const APPROVAL_LEAD_DAYS = 30;

/**
 * Retorna a data de início das férias (menor startDate dos segmentos).
 * Sem ordenação pressuposta no array.
 */
export function resolveRequestStartDate(
    segments: Array<{ startDate?: any; endDate?: any }> | null | undefined
): Date | null {
    if (!Array.isArray(segments) || segments.length === 0) return null;

    let minStart: Date | null = null;
    for (const segment of segments) {
        const startDate = createDateSafe(segment?.startDate ?? undefined);
        if (startDate) {
            if (minStart === null || startDate < minStart) {
                minStart = startDate;
            }
        }
    }
    return minStart;
}

/**
 * Prazo de aprovação = início das férias − 30 dias.
 * Retorna null se não conseguir resolver a data de início.
 */
export function resolveApprovalDeadline(
    segments: Array<{ startDate?: unknown; endDate?: unknown }> | null | undefined
): Date | null {
    const startDate = resolveRequestStartDate(segments);
    if (!startDate) return null;
    return subDays(startDate, APPROVAL_LEAD_DAYS);
}

/**
 * Retorna a data de aprovação formatada em 'dd/MM/yyyy'.
 */
export function formatApprovalDeadline(
    segments: Array<{ startDate?: unknown; endDate?: unknown }> | null | undefined
): string | null {
    const deadline = resolveApprovalDeadline(segments);
    if (!deadline || !isValid(deadline)) return null;
    return format(deadline, 'dd/MM/yyyy');
}

/**
 * Retorna true se o status está aguardando uma decisão.
 */
export function isAwaitingDecision(status?: string | null): boolean {
    return status === 'PendingManager' || status === 'PendingDP' || status === 'PendingClient';
}

/**
 * Retorna o número de dias até o prazo de aprovação.
 * Positivo = futuro, 0 = hoje, negativo = passado.
 */
export function daysUntilApprovalDeadline(
    segments: Array<{ startDate?: unknown; endDate?: unknown }> | null | undefined,
    today: Date = new Date()
): number | null {
    const deadline = resolveApprovalDeadline(segments);
    if (!deadline) return null;
    return differenceInCalendarDays(deadline, today);
}
